import { useEffect, useRef, useState } from 'react';
import { Logger, ConfigService } from '@nextgen-web-framework/core';
import {
  useBreakpoint,
  Breakpoints,
  useTranslation,
  Tabs,
  TabDesign,
  useNextgenRouter,
} from '@nextgen-web-framework/all';
import { cmsApi } from '@commerce-webcomponents/sdk';
import ContentListingContainer from '../ContentListingContainer/ContentListingContainer';
import { CMSTransformerDataType } from '../../types/common';
import getContentData from '../../utils/getContentData';
import logObject from '../../utils/logObject';
import styles from './ContentTemplate.module.scss';

interface ContentTemplateProps {
  data?: CMSTransformerDataType;
  params?: CMSTransformerDataType;
  componentKey: string;
  uId: string;
  pagePath: string;
  leftNav: boolean;
  sortByPublishedDate?: boolean;
}

const logger = Logger.getInstance(logObject('[...slug].tsx', 'Amway-Resources'));

const ContentTemplate = ({
  data,
  params,
  componentKey,
  uId,
  pagePath,
  leftNav,
  sortByPublishedDate = false,
}: ContentTemplateProps) => {
  const configs = ConfigService.getConfigs();
  const t = useTranslation();
  const router = useNextgenRouter();
  const elementRef = useRef<HTMLSpanElement>(null);
  const viewPortType = useBreakpoint() as Breakpoints;
  const isMobileView = [Breakpoints.XS, Breakpoints.SM].includes(viewPortType);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [initialFetch, setInitialFetch] = useState(true);
  const [parentCategory, subCategory] = params.slug;
  const FILTER_ALL_ID = 'all';
  const fetchPill = Array.isArray(data?.categories) && data?.categories.length > 0;
  let pillData: CMSTransformerDataType = [];
  if (fetchPill) {
    const initialSectionId = data?.all_category && !subCategory ? FILTER_ALL_ID : subCategory;
    pillData = getContentData({
      t,
      data: data,
      all: data.all_category,
      initialSectionId,
    });
  }
  const { pillTabData, initialSelectedId } = pillData;
  const [currentPill, setCurrentPill] = useState(initialSelectedId);
  const [contentData, setContentData] = useState<{ data: CMSTransformerDataType[]; count: number }>({
    data: [],
    count: 0,
  });

  const onTabClick = (tabIndex: number) => {
    let selectedTab = pillTabData[tabIndex].id;
    if (selectedTab === FILTER_ALL_ID) {
      selectedTab = '';
    }

    let path = `/${pagePath}/${parentCategory}/${selectedTab}`;

    const targetElement = document.getElementById('content-template');
    if (targetElement && targetElement.scrollLeft) {
      path = `${path}?offset=${targetElement?.scrollLeft}`;
    }

    router.push(path);
    setCurrentPill(tabIndex);
  };

  useEffect(() => {
    if (router.query?.has('offset')) {
      const offsetParam = parseInt(router.query?.get('offset') ?? '');
      if (offsetParam) {
        const targetElement = document.getElementById('content-template');
        if (targetElement) {
          targetElement.scrollTo(offsetParam, 0);
        }
      }
    }
  }, []);

  const fetchContentCards = (currentPage: number, fetchCount: boolean) => {
    setLoading(true);
    const currentCategory = pillTabData?.[currentPill]?.id === FILTER_ALL_ID ? '' : pillTabData?.[currentPill]?.id;
    const category = currentCategory || data?.categories?.[0]?.slug;
    const query =
      !fetchPill || (data?.all_category && !currentCategory)
        ? {
            parent_category: { $in_query: { url: data?.parent_category?.[0]?.url } },
          }
        : {
            $and: [
              { parent_category: { $in_query: { url: data?.parent_category?.[0]?.url } } },
              { content_category: { $in_query: { slug: category } } },
            ],
          };
    cmsApi
      .fetchAllContent({
        params: [
          { key: 'query', value: JSON.stringify(query) },
          { key: 'offset', value: (currentPage * 10).toString() },
          { key: 'limit', value: '10' },
          { key: 'includeCount', value: fetchCount.toString() },
          ...(configs?.NEXT_RESOURCES_SORTING_ENABLED && sortByPublishedDate
            ? [{ key: 'sort_by', value: 'published_date' }]
            : []),
        ],
        uId: uId,
        includeCount: true,
      })
      .then((result: CMSTransformerDataType) => {
        const cardsData = result?.data;
        if (Array.isArray(cardsData) && cardsData.length < 10) {
          setHasMore(false);
        }
        setContentData((prevData) => ({
          data: [...prevData.data, ...cardsData],
          count: prevData.count !== 0 ? prevData.count : result?.count,
        }));
        setPage((prevPage) => prevPage + 1);
      })
      .catch((err) => {
        logger.error(`${componentKey} API FAILURE - `, err);
      })
      .finally(() => {
        setLoading(false);
        setInitialFetch(false);
      });
  };
  const handleIntersection = (entries: IntersectionObserverEntry[]) => {
    const [firstEntry] = entries;
    if (firstEntry.isIntersecting && hasMore) {
      fetchContentCards(page, false);
    }
  };
  const resetCards = () => {
    setContentData({ data: [], count: 0 });
    setPage(0);
    setHasMore(true);
  };
  useEffect(() => {
    resetCards();
    fetchContentCards(0, true);
  }, [currentPill, data?.url]);

  useEffect(() => {
    if (currentPill !== initialSelectedId) {
      setCurrentPill(initialSelectedId);
    }
  }, [initialSelectedId]);
  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection);
    if (observer && elementRef.current) {
      observer.observe(elementRef.current);
    }
    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [contentData]);

  useEffect(() => {
    if (router.params?.locale) {
      resetCards();
      !initialFetch && fetchContentCards(0, true);
    }
  }, [router.params?.locale]);

  return (
    <div
      data-testid="content-template-testid"
      className={!leftNav ? styles['content-template-container'] : styles['content-template-container-left-nav']}
    >
      {!isMobileView && <div className={styles['content-template-banner']}>{data?.title}</div>}
      {pillTabData && (
        <div className={styles['content-template-section']} id="content-template">
          <Tabs
            design={TabDesign.PILL}
            onTabClick={(tabIndex) => onTabClick(tabIndex)}
            selectedTab={currentPill}
            tabList={pillTabData}
            className={styles['template-pill-tabs-container']}
          />
        </div>
      )}
      <div className={styles['content-template-data']}>
        {contentData && (
          <ContentListingContainer
            ref={elementRef}
            contentData={contentData.data}
            count={contentData.count}
            componentKey={componentKey}
            leftNav={leftNav}
            loading={loading}
            hasMore={hasMore}
          />
        )}
      </div>
    </div>
  );
};

export default ContentTemplate;
